
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  activities: string[];
  challenges: string[];
  achievements: string[];
}

export default function SosialisasiPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 50,
      realisasi: 42,
      percentage: 84,
      activities: ['Community meetings in 15 villages', 'Awareness campaigns through local media', 'Training for community leaders'],
      challenges: ['Initial skepticism from communities', 'Language barriers in remote areas', 'Limited access to some coastal villages'],
      achievements: ['42 successful socialization events', '1,250 community members reached', 'Formation of 8 community groups']
    },
    {
      year: '2022',
      target: 75,
      realisasi: 71,
      percentage: 94.7,
      activities: ['Expanded outreach to 28 villages', 'Mobile education units deployment', 'Youth engagement programs'],
      challenges: ['COVID-19 restrictions', 'Seasonal weather disruptions', 'Competing priorities in communities'],
      achievements: ['71 socialization sessions completed', '2,840 participants engaged', 'Established 15 local champion networks']
    },
    {
      year: '2023',
      target: 100,
      realisasi: 96,
      percentage: 96,
      activities: ['Comprehensive education campaigns', 'Digital outreach initiatives', 'Inter-village knowledge sharing'],
      challenges: ['Maintaining engagement momentum', 'Addressing misinformation', 'Resource allocation optimization'],
      achievements: ['96 education events conducted', '4,200 community members trained', '25 peer educator networks active']
    },
    {
      year: '2024',
      target: 120,
      realisasi: 115,
      percentage: 95.8,
      activities: ['Advanced training modules', 'Community-led initiatives', 'Regional knowledge exchanges'],
      challenges: ['Scaling quality with quantity', 'Ensuring long-term sustainability', 'Measuring behavioral change'],
      achievements: ['115 advanced sessions delivered', '5,680 participants certified', '35 community-led programs launched']
    }
  ];

  const impactMetrics = [
    { label: 'Total Participants Reached', value: '14,970', unit: 'People', icon: 'ri-group-line', color: '#2D8B57' },
    { label: 'Community Groups Formed', value: '83', unit: 'Groups', icon: 'ri-team-line', color: '#20B2AA' },
    { label: 'Villages Covered', value: '156', unit: 'Villages', icon: 'ri-home-line', color: '#8FBC8F' },
    { label: 'Awareness Level Increase', value: '78%', unit: 'Improvement', icon: 'ri-brain-line', color: '#90EE90' }
  ];

  const methodologySteps = [
    {
      step: 1,
      title: 'Community Mapping',
      description: 'Identifying key stakeholders and community leaders in target areas',
      icon: 'ri-map-line',
      color: '#2D8B57'
    },
    {
      step: 2,
      title: 'Cultural Adaptation',
      description: 'Tailoring messages to local customs, languages, and communication preferences',
      icon: 'ri-translate-line',
      color: '#20B2AA'
    },
    {
      step: 3,
      title: 'Multi-Channel Approach',
      description: 'Using various communication channels: meetings, media, demonstrations, and digital platforms',
      icon: 'ri-broadcast-line',
      color: '#8FBC8F'
    },
    {
      step: 4,
      title: 'Participatory Learning',
      description: 'Engaging communities in interactive sessions with hands-on activities and discussions',
      icon: 'ri-discuss-line',
      color: '#90EE90'
    },
    {
      step: 5,
      title: 'Follow-up & Reinforcement',
      description: 'Regular follow-up sessions to reinforce messages and address emerging questions',
      icon: 'ri-refresh-line',
      color: '#2D8B57'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#2D8B57] to-[#20B2AA] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-presentation-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Socialization Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                Building awareness and community engagement for mangrove conservation through comprehensive education and outreach initiatives
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                >
                  <i className="ri-download-line mr-2"></i>
                  Download Report
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                >
                  <i className="ri-eye-line mr-2"></i>
                  View Activities
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Progress & Targets', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Impact Assessment', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Methodology', icon: 'ri-file-text-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#2D8B57] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Socialization Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Comprehensive community education and awareness building for sustainable mangrove conservation
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-book-open-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">The Story Behind the Numbers</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        When our team first arrived in Desa Karawang in early 2021, the village elder, Pak Sardi, greeted us with skepticism. "Another program promising change," he said, shaking his head. The community had heard it all before—promises of environmental restoration that never materialized, education programs that disappeared after a few months.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/58fadf92ac269190d544d6273ae77dad.jpg"
                            alt="Village meeting in Karawang"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Pak Sardi addressing the community during our first socialization session in Karawang, 2021
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#2D8B57] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "We had lost faith in outsiders. Our mangroves were dying, our fish were disappearing, and no one seemed to care about our traditional knowledge."
                            </p>
                            <footer className="text-[#2D8B57] font-bold">— Pak Sardi, Village Elder</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Three years later, Pak Sardi leads one of our most successful community groups. His transformation from skeptic to champion illustrates the power of genuine community engagement and culturally sensitive education approaches.
                          </p>
                        </div>
                      </div>
                      
                      <h4 className="text-xl font-bold text-[#333333] mb-4">From Resistance to Leadership</h4>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        The breakthrough came during our third visit. Instead of presenting PowerPoint slides about mangrove benefits, we asked the elders to share their memories of the coast from decades past. Pak Sardi's eyes lit up as he described waters so clear you could see fish swimming, mangrove forests so thick that children could play hide-and-seek among the roots.
                      </p>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">The Turning Point: Traditional Knowledge Meets Modern Science</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          Our socialization approach shifted from teaching to learning. We documented traditional ecological knowledge, validated it with scientific research, and created education materials that honored both perspectives. This collaborative approach transformed skeptics into partners.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#2D8B57] mb-1">89%</div>
                            <div className="text-xs text-gray-600">Knowledge retention rate</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#20B2AA] mb-1">156</div>
                            <div className="text-xs text-gray-600">Villages engaged</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#8FBC8F] mb-1">83</div>
                            <div className="text-xs text-gray-600">Community groups formed</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Field Report: Measuring Hearts and Minds</h3>
                        <p className="text-gray-600">An investigative look at socialization program effectiveness</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#20B2AA] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Field Report</strong> • December 2024 • M4CR Research Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          How Do You Measure a Change of Heart? The Science Behind Community Transformation
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>DEMAK, Central Java</strong> — At 6 AM on a humid Tuesday morning, Bu Yanti stands before 40 fishermen in the village community center, her voice carrying across the room as she explains the connection between healthy mangroves and abundant fish catches. Three years ago, she couldn't speak in public. Today, she's one of our most effective community educators.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        <div className="lg:col-span-2">
                          <p className="text-gray-700 leading-relaxed mb-4">
                            Bu Yanti's transformation represents more than individual growth—it's a measurable shift in community capacity that our socialization program tracks through rigorous assessment methods. But how do you quantify a change in mindset? How do you measure the moment when skepticism turns to ownership?
                          </p>
                          
                          <p className="text-gray-700 leading-relaxed mb-4">
                            Our research team has spent four years developing metrics that capture both the quantitative and qualitative dimensions of community engagement. The results reveal a sophisticated story of social change that goes far beyond simple attendance numbers.
                          </p>
                          
                          <h5 className="font-bold text-[#333333] mb-3">The Methodology: Beyond Head Counts</h5>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            Traditional development programs measure success through participation rates and knowledge tests. Our approach digs deeper, tracking behavioral indicators that predict long-term conservation outcomes:
                          </p>
                        </div>
                        
                        <div className="bg-[#F8F9FA] rounded-lg p-4">
                          <h6 className="font-bold text-[#333333] mb-3">Key Indicators We Track</h6>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start">
                              <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5"></i>
                              Voluntary participation rates
                            </li>
                            <li className="flex items-start">
                              <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5"></i>
                              Peer-to-peer knowledge sharing
                            </li>
                            <li className="flex items-start">
                              <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5"></i>
                              Community-initiated activities
                            </li>
                            <li className="flex items-start">
                              <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5"></i>
                              Leadership emergence patterns
                            </li>
                            <li className="flex items-start">
                              <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5"></i>
                              Behavioral change adoption
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-blue-800 mb-4">Data Deep Dive: What the Numbers Really Tell Us</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Engagement Evolution (2021-2024)</h6>
                            <div className="space-y-3">
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Passive Attendance</span>
                                  <span>2021: 78% → 2024: 23%</span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '23%' }}></div>
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Active Participation</span>
                                  <span>2021: 22% → 2024: 77%</span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '77%' }}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Knowledge Application</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• 68% implement sustainable practices</li>
                              <li>• 82% share knowledge with neighbors</li>
                              <li>• 45% take leadership roles</li>
                              <li>• 91% report increased environmental awareness</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <h5 className="font-bold text-[#333333] mb-3">The Ripple Effect: When Communities Become Teachers</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Perhaps the most significant indicator of program success is the emergence of community-led education initiatives. In 35 villages, local champions now conduct their own socialization sessions, adapting our materials to local contexts and reaching audiences we never could.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h6 className="font-bold text-green-800 mb-3">Community-Led Initiatives</h6>
                          <ul className="space-y-2 text-sm text-green-700">
                            <li>• 35 villages with local education programs</li>
                            <li>• 127 community educators trained</li>
                            <li>• 2,340 people reached through peer education</li>
                            <li>• 89% knowledge retention in peer-taught sessions</li>
                          </ul>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h6 className="font-bold text-yellow-800 mb-3">Innovation Adaptations</h6>
                          <ul className="space-y-2 text-sm text-yellow-700">
                            <li>• Traditional puppet shows with conservation themes</li>
                            <li>• Youth-led social media campaigns</li>
                            <li>• Women's group integration with livelihood training</li>
                            <li>• Elder storytelling circles preserving traditional knowledge</li>
                          </ul>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#20B2AA] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "The real success isn't in our numbers—it's in the fact that communities no longer need us to continue the education. They've become the teachers."
                        </p>
                        <footer className="text-[#20B2AA] font-bold">— Dr. Sarah Wijaya, M4CR Program Director</footer>
                      </blockquote>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Challenges That Shaped Our Approach</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Our data also reveals the obstacles that forced us to evolve. Early sessions with low engagement taught us that one-size-fits-all approaches don't work in diverse coastal communities. Language barriers, competing priorities, and historical mistrust of outsiders required constant adaptation.
                      </p>
                      
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <h6 className="font-bold text-red-800 mb-3">Lessons from Setbacks</h6>
                        <p className="text-red-700 text-sm mb-3">
                          In 2022, our program faced a 40% drop in attendance during the post-COVID recovery period. This crisis forced us to innovate with mobile education units and digital outreach, ultimately strengthening our approach.
                        </p>
                        <div className="text-sm text-red-700">
                          <strong>Key Learning:</strong> Resilient programs must be flexible programs. Our ability to adapt quickly turned a potential failure into our most innovative year.
                        </div>
                      </div>
                      
                      <h5 className="font-bold text-[#333333] mb-3">Looking Forward: The Next Chapter</h5>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        As we prepare for program expansion, the socialization component has evolved from a supporting activity to the foundation of everything we do. The data shows that communities with strong education programs achieve 40% higher restoration success rates and 60% better long-term sustainability.
                      </p>
                      
                      <p className="text-gray-700 leading-relaxed">
                        Bu Yanti's morning education session in Demak isn't just a program activity—it's proof that when communities understand the 'why' behind conservation, they become unstoppable forces for change. The numbers tell the story, but the real impact lives in the hearts and minds of 14,970 people who now see themselves as guardians of their coastal heritage.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#2D8B57]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Raise Environmental Awareness</h4>
                          <p className="text-gray-600 text-sm">
                            Educate coastal communities about the importance of mangrove ecosystems and their role in climate resilience
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#20B2AA]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Build Community Ownership</h4>
                          <p className="text-gray-600 text-sm">
                            Foster a sense of responsibility and ownership among local communities for mangrove conservation
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#8FBC8F]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Promote Behavioral Change</h4>
                          <p className="text-gray-600 text-sm">
                            Encourage adoption of sustainable practices and active participation in conservation activities
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#90EE90]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Strengthen Social Capital</h4>
                          <p className="text-gray-600 text-sm">
                            Build networks and partnerships among community members, leaders, and organizations
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Key Activities</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Community Meetings</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Regular village meetings to discuss mangrove conservation, share knowledge, and plan community actions
                        </p>
                        <div className="text-xs text-[#2D8B57] font-medium">324 meetings conducted across 156 villages</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Educational Campaigns</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Multi-media awareness campaigns using local radio, posters, and digital platforms
                        </p>
                        <div className="text-xs text-[#20B2AA] font-medium">89 campaigns reaching 50,000+ people</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Training Programs</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Capacity building sessions for community leaders and environmental champions
                        </p>
                        <div className="text-xs text-[#8FBC8F] font-medium">156 training sessions with 2,340 participants</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Youth Engagement</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Special programs targeting young people through schools and youth organizations
                        </p>
                        <div className="text-xs text-[#90EE90] font-medium">78 youth programs engaging 3,200 students</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20coastal%20community%20leader%20speaking%20at%20village%20meeting%2C%20mangrove%20conservation%20discussion%2C%20community%20engagement%2C%20traditional%20Indonesian%20village%20setting%2C%20environmental%20education%20session%2C%20local%20leader%20presentation&width=60&height=60&seq=community-leader-1&orientation=squarish"
                          alt="Community Leader"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Sardi</div>
                          <div className="text-sm text-gray-600">Village Leader, Karawang</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "The socialization program opened our eyes to the true value of mangroves. Now our entire village is actively involved in conservation efforts, we've become a model for other communities."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20female%20teacher%20conducting%20environmental%20education%2C%20mangrove%20awareness%20program%2C%20coastal%20community%20education%2C%20Indonesian%20woman%20educator%2C%20environmental%20training%20session%2C%20community%20learning&width=60&height=60&seq=teacher-educator-1&orientation=squarish"
                          alt="Community Educator"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Bu Yanti</div>
                          <div className="text-sm text-gray-600">Community Educator, Demak</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Through the training programs, I learned how to effectively communicate environmental messages. Now I lead education sessions in five neighboring villages."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Progress Tracking & Target Achievement
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year journey of building community awareness and engagement
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Progress Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#2D8B57] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#2D8B57] to-[#20B2AA] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {data.realisasi}/{data.target} sessions
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#2D8B57] mb-1">324</div>
                        <div className="text-sm text-gray-600">Total Sessions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#20B2AA] mb-1">92.6%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#8FBC8F] mb-1">14,970</div>
                        <div className="text-sm text-gray-600">People Reached</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#2D8B57] to-[#20B2AA] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Achievement Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{data.target} sessions</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Realized:</span>
                              <span className="font-bold text-[#2D8B57]">{data.realisasi} sessions</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#2D8B57] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Key Activities</h4>
                              <ul className="space-y-2">
                                {data.activities.map((activity, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Challenges</h4>
                              <ul className="space-y-2">
                                {data.challenges.map((challenge, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-alert-line text-[#FF6B6B] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{challenge}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Achievements</h4>
                              <ul className="space-y-2">
                                {data.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-trophy-line text-[#FFD93D] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Impact Assessment & Outcomes
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring the transformative effects of socialization efforts on communities
                  </p>
                </div>

                {/* Impact Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Behavioral Change Assessment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Behavioral Change Indicators</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Environmental Awareness</span>
                          <span className="font-bold text-[#2D8B57]">89%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#2D8B57] h-2 rounded-full" style={{ width: '89%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Increase in environmental knowledge and awareness</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Active Participation</span>
                          <span className="font-bold text-[#20B2AA]">76%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#20B2AA] h-2 rounded-full" style={{ width: '76%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Community members actively participating in conservation</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Sustainable Practices</span>
                          <span className="font-bold text-[#8FBC8F]">68%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#8FBC8F] h-2 rounded-full" style={{ width: '68%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Adoption of environmentally sustainable practices</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Community Leadership</span>
                          <span className="font-bold text-[#90EE90]">82%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#90EE90] h-2 rounded-full" style={{ width: '82%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Emergence of local environmental champions</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Community Feedback</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-thumb-up-line text-[#2D8B57]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Program Satisfaction</div>
                            <div className="text-2xl font-bold text-[#2D8B57]">94.2%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Participants rate the socialization program as highly effective and relevant
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-message-line text-[#20B2AA]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Knowledge Retention</div>
                            <div className="text-2xl font-bold text-[#20B2AA]">87.5%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Follow-up assessments show high retention of environmental knowledge
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-share-line text-[#8FBC8F]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Knowledge Sharing</div>
                            <div className="text-2xl font-bold text-[#8FBC8F]">73.8%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Participants actively share knowledge with family and neighbors
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Long-term Impact */}
                <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Long-term Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-community-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Community Empowerment</h4>
                      <p className="text-gray-600 text-sm">
                        83 community groups now independently organize conservation activities and advocate for environmental protection
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-graduation-cap-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Knowledge Transfer</h4>
                      <p className="text-gray-600 text-sm">
                        Local champions now conduct their own education sessions, creating a sustainable knowledge transfer system
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-seedling-line text-2xl text-[#8FBC8F]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Behavioral Change</h4>
                      <p className="text-gray-600 text-sm">
                        Measurable shifts in environmental behavior, with 68% of participants adopting sustainable practices
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Socialization Methodology
                  </h2>
                  <p className="text-lg text-gray-600">
                    Systematic approach to building community awareness and engagement
                  </p>
                </div>

                {/* Methodology Steps */}
                <div className="space-y-8 mb-12">
                  {methodologySteps.map((step, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                          style={{ backgroundColor: step.color }}
                        >
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-4">
                            <i className={`${step.icon} text-2xl mr-3`} style={{ color: step.color }}></i>
                            <h3 className="text-2xl font-bold text-[#333333]">{step.title}</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tools and Techniques */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Communication Tools</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-mic-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Community Meetings</h4>
                          <p className="text-sm text-gray-600">Face-to-face discussions and presentations</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-radio-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Local Media</h4>
                          <p className="text-sm text-gray-600">Radio programs and community bulletins</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center">
                          <i className="ri-image-line text-[#8FBC8F]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Visual Materials</h4>
                          <p className="text-sm text-gray-600">Posters, banners, and infographics</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#90EE90]/10 rounded-full flex items-center justify-center">
                          <i className="ri-smartphone-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Digital Platforms</h4>
                          <p className="text-sm text-gray-600">Social media and messaging apps</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Engagement Techniques</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-discuss-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Interactive Discussions</h4>
                          <p className="text-sm text-gray-600">Two-way communication and Q&A sessions</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-hand-heart-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Hands-on Activities</h4>
                          <p className="text-sm text-gray-600">Practical demonstrations and exercises</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center">
                          <i className="ri-drama-line text-[#8FBC8F]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Storytelling</h4>
                          <p className="text-sm text-gray-600">Local stories and case studies</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#90EE90]/10 rounded-full flex items-center justify-center">
                          <i className="ri-group-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Peer Learning</h4>
                          <p className="text-sm text-gray-600">Community-to-community knowledge sharing</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Evaluation Framework */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Evaluation Framework</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-survey-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Pre/Post Assessments</h4>
                      <p className="text-gray-600 text-sm">
                        Knowledge and attitude surveys before and after socialization sessions to measure learning outcomes
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-feedback-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Participant Feedback</h4>
                      <p className="text-gray-600 text-sm">
                        Regular feedback collection to improve program delivery and ensure relevance to community needs
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#8FBC8F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-line-chart-line text-2xl text-[#8FBC8F]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Behavioral Tracking</h4>
                      <p className="text-gray-600 text-sm">
                        Long-term monitoring of behavioral changes and community engagement in conservation activities
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#2D8B57] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Join Our Community Education Movement
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Be part of the transformation. Learn how you can contribute to mangrove conservation through community engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
